/**
 * Detail collapse for a holiday.
 */
import * as React from 'react';
import { Palmtree, Calendar, Info, XCircle } from 'lucide-react';
import { DetailBadge } from '@/components/common/details/DetailBadge';
import { DetailField } from '@/components/common/details/DetailField';
import { DetailsCard } from '@/components/common/DetailsCard';
import { deriveHolidayDetailDisplay } from '@/components/holidays/holiday-table-utils';
import type { Holiday } from '@/types/holiday.types';

/** Detail collapse sections for a holiday (header + dates / zone / metadata). */
export const renderHolidayDetails = (holiday: Holiday): React.ReactNode => {
  const {
    typeConfig,
    TypeIcon,
    typeColorName,
    vacationZoneDetail,
    startDateLong,
    endDateLong,
    createdAtLabel,
    updatedAtLabel,
  } = deriveHolidayDetailDisplay(holiday);

  return (
    <>
      <div className="details-view-header">
        <div className="details-view-header-left">
          <div className="details-view-header-icon">
            <Palmtree className="w-6 h-6 text-[var(--color-2-nuance-1)]" />
          </div>
          <div className="details-view-header-content">
            <div className="details-view-header-with-badges">
              <h3 className="details-view-header-title">{holiday.name}</h3>
              <DetailBadge
                label={typeConfig.label}
                icon={TypeIcon}
                colorName={typeColorName}
                variant="filled"
              />
              {holiday.isRecurringYearly ? (
                <DetailBadge
                  label="Récurrent"
                  icon={Calendar}
                  colorName="color-1"
                  variant="outlined"
                />
              ) : (
                <DetailBadge
                  label="Non récurrent"
                  icon={XCircle}
                  colorName="color-1"
                  variant="outlined"
                />
              )}
            </div>
            <p className="details-view-header-subtitle">{typeConfig.label}</p>
          </div>
        </div>
      </div>

      <div className="details-view-grid">
        <DetailsCard icon={Info} title="Informations générales" colorName="color-2" colSpan={1}>
          <div className="space-y-3">
            <DetailField label="Nom" value={holiday.name} />
            {vacationZoneDetail && (
              <DetailField label="Zone vacances scolaires" value={vacationZoneDetail} />
            )}
            {holiday.description && (
              <DetailField label="Description" value={holiday.description} vertical />
            )}
          </div>
        </DetailsCard>

        <DetailsCard icon={Calendar} title="Période" colorName="color-1" colSpan={1}>
          <div className="grid grid-cols-2 gap-4">
            <DetailField label="Date de début" value={startDateLong} vertical />
            <DetailField label="Date de fin" value={endDateLong} vertical />
          </div>
        </DetailsCard>

        {holiday.createdAt && (
          <DetailsCard icon={Calendar} title="Informations système" colorName="color-3" colSpan={1}>
            <div className="grid grid-cols-2 gap-4">
              <DetailField label="Créé le" value={createdAtLabel} />
              {updatedAtLabel && <DetailField label="Modifié le" value={updatedAtLabel} />}
            </div>
          </DetailsCard>
        )}
      </div>
    </>
  );
};
